import {AfterImportProcessResult} from 'app/main/private/setup/import-data/model/import-result.model';
import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {UnsubscribeDirective} from 'app/common/unsubscribe.directive';
import {ImportResult} from 'app/main/private/setup/import-data/model/import-result.model';
import {getObjectKeys} from 'app/common/utils/object-utils';
import {faTimesCircle, faCheckCircle} from '@fortawesome/free-solid-svg-icons';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';
import {locale as english} from 'app/common/import-result-view/i18n/en';
import {locale as french} from 'app/common/import-result-view/i18n/fr';

@Component({
    // Angular 22 defaults to OnPush; Eager preserves the previous behaviour.
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'import-result-view',
    templateUrl: './import-result-view.component.html',
    styleUrls: ['./import-result-view.component.scss'],
    standalone: false
})
export class ImportResultViewComponent extends UnsubscribeDirective {
    @Input() uploadResult: ImportResult;

    faFail = faTimesCircle;
    faPass = faCheckCircle;

    resultsKey = 'view.resultsText';
    rowsKey = 'view.rowsText';
    rowKey = 'view.rowText';

    readonly AFTER_IMPORT_PROCESSOR_TYPE_SUCCESS_MESSAGE_RETRIEVERS = new Map<string, (result: AfterImportProcessResult) => string>([
        ['CREATE_TOUR_PLAN_SUGGESTED_RUNS', this.formatCreatedTourPlanSuggestedRunsProcessorResultMessage],
        ['CREATE_SUBCONTRACTED_RUNS', this.formatCreatedSubcontractedRunsProcessorResultMessage]
    ]);

    constructor(private translationLoader: FuseTranslationLoaderService) {
        super();
        this.translationLoader.loadTranslations(english, french);
    }

    getResultKeys(): any {
        return getObjectKeys(this.uploadResult);
    }

    getCategoryStyle(category: string): any {
        const style = {
            'border-radius': '5px',
            padding: '2px 5px'
        };
        const catResult = this.uploadResult[category];
        if (catResult.categoryFail) {
            style['background-color'] = '#ffebe6';
            style['color'] = '#bf2600';
        } else if (catResult.report.metadata.total === catResult.report.metadata.success) {
            style['background-color'] = '#e3fcef';
            style['color'] = '#064';
        } else {
            style['background-color'] = '#dfe1e6';
            style['color'] = '#42526e';
        }
        return style;
    }

    getSanitizedName = (name: string): string => {
        return name.replace(/_/g, ' ');
    };

    getFailureResultsForCategory(category: string): any[] {
        return this.uploadResult[category].report.results.filter((result) => result.status != 200);
    }

    getAfterImportProcessResultMessage(result: AfterImportProcessResult) {
        if (!result.success) {
            return result.error;
        }
        if (!this.AFTER_IMPORT_PROCESSOR_TYPE_SUCCESS_MESSAGE_RETRIEVERS.has(result.processorType)) {
            return 'Success!';
        }
        return this.AFTER_IMPORT_PROCESSOR_TYPE_SUCCESS_MESSAGE_RETRIEVERS.get(result.processorType)(result);
    }

    private formatCreatedTourPlanSuggestedRunsProcessorResultMessage(result: AfterImportProcessResult) {
        let msg = '';
        const runIds = result.data?.runIds ?? [];
        if (runIds.length == 0) {
            msg += 'No runs created. ';
        } else {
            msg += `Runs created: ${result.data?.runIds?.join(', ')}. `;
        }
        const unassignedOrders = result.data?.unassignedOrders ?? [];
        if (unassignedOrders.length == 0) {
            msg += `All orders assigned to runs.`;
        } else {
            msg += `Following orders could not be assigned to runs: `;
            msg += unassignedOrders.map((order) => `${order.jobId} [${order.reasons.map((reason) => reason.code).join(', ')}]`).join(', ');
        }
        return msg;
    }

    // Runs created already sub-contracted, for orders whose imported expense names a subcontractor.
    private formatCreatedSubcontractedRunsProcessorResultMessage(result: AfterImportProcessResult) {
        const runIds = result.data?.runIds ?? [];
        if (runIds.length == 0) {
            return 'No sub-contracted runs created.';
        }
        return `Sub-contracted runs created: ${runIds.join(', ')}.`;
    }
}
